import { Component, ElementRef, Input, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core'
import { Subject } from 'rxjs'
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators'
import { ClientTopServices, MatrixServices } from '../../services'

export interface ImageObservableInterface {
    imageUrl: string;
    matrix: any;
    style: any;
}

@Component({
    selector: 'app-image',
    templateUrl: './image.component.html',
    styleUrls: ['./image.component.css'],
})
export class ImageComponent implements OnInit, OnDestroy {
    initialize = false
    loading = false
    fetchedImage: boolean = false
    imageObservable: Subject<ImageObservableInterface> = new Subject<ImageObservableInterface>()
    image: HTMLImageElement
    removeImage: HTMLImageElement
    imageDestroy$: Subject<boolean> = new Subject<boolean>()
    timeForShowLoading: number = 1000
    currentUrl: string
    imageIndex = 0
    @Input() imageContainer: ElementRef
    @Input() isShowDevice = false
    @ViewChild('wrapper', { read: false }) wrapper: ElementRef

    constructor(
        private renderer: Renderer2,
        private clientTopServices: ClientTopServices,
        private matrixServices: MatrixServices,
    ) {
    }

    ngOnInit() {
        this.imageObservable
        .pipe(
            debounceTime(400),
            distinctUntilChanged(),
            takeUntil(this.imageDestroy$),
        )
        .subscribe((image) => {
            if (image) {
                this.prependImage(
                    image.imageUrl,
                    image.matrix,
                    image.style,
                )
            }
        })
    }

    ngOnDestroy(): void {
        if(this.imageDestroy$){
            this.imageDestroy$.next(true)
            this.imageDestroy$.unsubscribe()
        }
    }

    prependImage(src, matrix, style, className = 'img-hide') {
        if (this.currentUrl != src) {
            this.currentUrl = src
            if (this.wrapper) {
                // remove image
                if (this.image) {
                    if (!src) {
                        this.loading = false
                        this.removeImage = this.image
                        this.removeOldImage('hiding-fast', 300)
                    } else {
                        if (this.removeImage) {
                            this.renderer.removeChild(
                                this.wrapper.nativeElement,
                                this.removeImage,
                            )
                            this.removeImage = null
                        }
                        this.removeImage = this.image
                    }
                }
                if (src) {
                    setTimeout(() => {
                        if (this.image.className !== 'showing') {
                            this.loading = true
                        }
                    }, this.timeForShowLoading)
                    this.fetchedImage = false
                    this.image = this.renderer.createElement('img')
                    this.image.src = src
                    this.image.className = className
                    this.image.onerror = () => this.handleError()
                    this.image.onload = () => this.loadedImage(matrix, style)
                    this.renderer.appendChild(
                        this.wrapper.nativeElement,
                        this.image,
                    )
                }
            }
        } else {
            this.loadedImage(matrix, style)
        }
    }

    loadedImage(matrixStyle, style) {
        this.reRenderMatrixInitial(this.image, matrixStyle)
        this.reRenderStyleImage(style)
        this.image.className = 'showing'
        setTimeout(() => {
            this.fetchedImage = true
        }, 200)

        this.removeOldImage()
        this.loading = false
    }

    reRenderStyleImage(style) {
        if (style) {
            const rotate = (style.rotate ? style.rotate : 0)
            const scaleX = (style.scaleX ? style.scaleX : 1)
            const scaleY = (style.scaleY ? style.scaleY : 1)
            const stringTranform = ' rotate(' + rotate + 'deg) scaleX(' + scaleX + ') scaleY(' + scaleY + ')'
            this.image.style.transform += stringTranform
        }
    }

    removeOldImage(className = 'hiding', timeout = 1000) {
        if (this.removeImage) {
            this.removeImage.className = className
            setTimeout(() => {
                if (this.removeImage) {
                    this.renderer.removeChild(
                        this.wrapper.nativeElement,
                        this.removeImage,
                    )
                    this.removeImage = null
                }
                this.fetchedImage = true
            }, timeout)
        }
    }

    handleError() {
        const urlSerializer = new URL(this.image.src)
        const currentType = urlSerializer.searchParams.get('type')
        switch (currentType) {
            case 'large':
                urlSerializer.searchParams.set('type', 'xlarge')
                this.image.src = urlSerializer.toString()
                break
            case 'xlarge':
                urlSerializer.searchParams.set('type', 'other')
                this.image.src = urlSerializer.toString()
                break
            case 'other':
                urlSerializer.searchParams.set('type', 'medium')
                this.image.src = urlSerializer.toString()
                break
            case 'medium':
                urlSerializer.searchParams.set('type', '')
                this.image.src = urlSerializer.toString()
                break
            default:
                // urlSerializer.searchParams.set("type", "medium");
                break
        }
    }

    private reRenderMatrix(matrixStyle) {
        this.image.style.transform = matrixStyle
    }

    reRenderMatrixInitial(img, arrayMatrix) {
        const wrapper = this.wrapper.nativeElement
        let widthT = wrapper.offsetWidth
        let heightT = wrapper.offsetHeight
        let scaleX = widthT / img.clientWidth
        let scaleY = heightT / img.clientHeight
        let scaleXY = 1
        if (scaleX > scaleY) {
            scaleXY = scaleY
        } else {
            scaleXY = scaleX
        }
        let n1 = scaleXY * arrayMatrix[0]
        let n2 = scaleXY * arrayMatrix[1]
        let n3 = scaleXY * arrayMatrix[2]
        let n4 = scaleXY * arrayMatrix[3]
        let n5 = (widthT - img.clientWidth) / 2
        let n6 = (heightT - img.clientHeight) / 2
        this.image.style.transform =
            'matrix(' +
            n1 +
            ', ' +
            n2 +
            ', ' +
            n3 +
            ', ' +
            n4 +
            ', ' +
            n5 +
            ', ' +
            n6 +
            ')'

    }
}



// WEBPACK FOOTER //
// ./src/app/commons/image/image.component.ts